// src/components/CookieBanner.tsx
//
// Lightweight cookie / privacy notice. Persists the user's choice in
// localStorage under `cookieConsent` so it isn't shown again. This is a
// presentational notice only — it does not gate any tracking script
// because the app currently runs no third-party analytics by default.
// When you add analytics/ads pixels, gate them behind
// `getCookieConsent() === 'accepted'`.

import React, { useEffect, useState } from 'react';
import './CookieBanner.css';

const STORAGE_KEY = 'cookieConsent';

export type CookieConsentValue = 'accepted' | 'rejected' | null;

export function getCookieConsent(): CookieConsentValue {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
}

const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getCookieConsent() === null) setVisible(true);
  }, []);

  const decide = (value: 'accepted' | 'rejected') => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore storage failures */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div role="dialog" aria-live="polite" aria-label="Cookie notice" className="cookie-banner">
      <div className="cookie-banner__text">
        We use essential cookies to keep you signed in and to remember your
        preferences. Optional analytics cookies are off by default. See our{' '}
        <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>{' '}
        for details.
      </div>
      <div className="cookie-banner__actions">
        <button
          type="button"
          className="cookie-banner__btn cookie-banner__btn--secondary"
          onClick={() => decide('rejected')}
        >
          Essential only
        </button>
        <button
          type="button"
          className="cookie-banner__btn cookie-banner__btn--primary"
          onClick={() => decide('accepted')}
        >
          Accept all
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
